import React, { useState, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import axios from 'axios';
import './Login.css';
import apiClient, { endpoints } from '../api';

const Login = ({ onLogin, onSwitchToRegister }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Clear form data when component mounts (after logout)
  useEffect(() => {
    setFormData({
      email: '',
      password: ''
    });
    setErrors({});
    setShowPassword(false);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Make API call to backend
      const response = await apiClient.post(endpoints.loginVendor, {
        email: formData.email,
        password: formData.password
      }, {
        timeout: 10000
      });

      if (response.status === 200) {
        const vendorData = {
          id: response.data.vendor.id,
          shopName: response.data.vendor.shopName,
          ownerName: response.data.vendor.ownerName,
          email: response.data.vendor.email,
          status: response.data.vendor.status,
          token: response.data.token
        };
        onLogin(vendorData);
      }
    } catch (error) {
      // If backend is not available, use demo mode for testing
      if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        // Demo mode - accept any email/password for testing
        console.log('Backend not available, using demo mode');
        const vendorData = {
          id: 1,
          shopName: 'Demo Shop',
          ownerName: 'Demo Owner',
          email: formData.email,
          status: 'SUBMITTED',
          token: 'demo-token'
        };
        onLogin(vendorData);
        return;
      }
      
      console.error('Login error:', error);
      
      if (error.response) {
        // Server responded with error status
        if (error.response.status === 401) {
          setErrors({ general: 'Invalid email or password' });
        } else if (error.response.status === 403) {
          // Handle approval status errors
          const errorData = error.response.data;
          if (errorData.status) {
            setErrors({ 
              general: `${errorData.error} Current status: ${errorData.statusLabel || errorData.status}` 
            });
          } else {
            setErrors({ general: errorData.error || 'Account not approved for login.' });
          }
        } else if (error.response.status === 500) {
          setErrors({ general: 'Server error. Please try again later.' });
        } else {
          setErrors({ general: error.response.data.error || 'Login failed. Please try again.' });
        }
      } else if (error.request) {
        // Request was made but no response received
        setErrors({ general: 'No response from server. Please check if backend is running.' });
      } else {
        // Something else happened
        setErrors({ general: 'Login failed. Please try again.' });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img src="/sonickart1.jpg" alt="SonicKart Logo" className="logo-image" />
            <h1>SonicKart</h1>
            <p className="login-tagline">From your store to every doorstep – with SonicKart</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {errors.general && (
            <div className="error-message general">
              <LucideIcons.AlertCircle size={16} />
              <span>{errors.general}</span>
            </div>
          )}

          <div className="form-group">
        
            <div className={`input-container ${errors.email ? 'error' : ''}`}>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                placeholder="Email Address"
                disabled={isLoading}
              />
            </div>
            {errors.email && (
              <span className="error-text">{errors.email}</span>
            )}
          </div>

          <div className="form-group">
          
            <div className={`input-container ${errors.password ? 'error' : ''}`}>
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                placeholder="Password"
                disabled={isLoading}
              />
              <button
                type="button"
                className="password-toggle"
                onClick={togglePasswordVisibility}
                disabled={isLoading}
              >
                {showPassword ? <LucideIcons.EyeOff size={20} /> : <LucideIcons.Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <span className="error-text">{errors.password}</span>
            )}
          </div>

          <div className="form-options">
            <label className="checkbox-container">
              <input type="checkbox" />
              <span className="checkmark"></span>
              Remember me
            </label>
            <a href="#" className="forgot-password">
              Forgot password?
            </a>
          </div>

          <button
            type="submit"
            className={`login-btn ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <div className="spinner"></div>
                Signing in...
              </>
            ) : (
              'Sign In'
            )}
          </button>
        </form>

        <div className="login-footer">
          <p>
            Don't have an account? 
            <button 
              type="button" 
              className="switch-link"
              onClick={onSwitchToRegister}
            >
              Register here
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
